/* Tour "forecast-formulas" — apprend à construire le prévisionnel central
   (cf. https://help.okimia.com/fr/articles/8823076), par la pratique :
   vue tableau → formule Fx → ajustement manuel → factures.

   Pour ne jamais toucher aux vraies catégories de l'utilisateur, on crée une
   sous-catégorie « bac à sable » au démarrage (onStart) et on la supprime à la
   sortie (onExit), quel que soit le mode de sortie. Un filet de récupération
   (cleanupOrphanSandbox) nettoie un éventuel résidu au prochain montage. */

import store from '@/store';
import AppBus from '@/bus/app-bus';
import {isTourCompleted} from './onboarding-state';
import './types';

const SANDBOX_KEY = 'fygr-tour-sandbox';
// L'API addNewCategory valide le nom et rejette emoji / parenthèses
// (« Veuillez vérifier votre saisie »). On reste donc en texte simple.
const SANDBOX_NAME = 'Exemple Okimia';
// Match tolérant (casse, emoji, parenthèses) : attrape l'ancien « Exemple
// (tutoriel) » / « 🧪 Exemple (tutoriel) » ET le nouveau « Exemple tutoriel »,
// pour que le cleanup purge aussi les orphelins de l'ancien nommage.
const isSandboxName = (n: unknown) =>
  typeof n === 'string' && /exemple\s*(okimia|\(?\s*tutoriel)/i.test(n);

type SandboxRef = {subId: number; parentId: number};

// SÉRIALISATION des opérations bac à sable. Le moteur n'attend PAS onExit/onStart
// (fire-and-forget) : en enchaînant deux tutos forecast, le cleanupSandbox() du
// précédent (suppression async de « Exemple Okimia ») tournerait EN MÊME TEMPS que
// le seedSandbox() du suivant → le seed réutilise la catégorie encore présente, puis
// la suppression en retard la détruit → l'IA pointe une catégorie fantôme (invisible).
// On fait donc passer seed & cleanup par une file : le cleanup finit toujours avant
// que le seed suivant ne démarre.
let sandboxQueue: Promise<unknown> = Promise.resolve();
function enqueueSandboxOp<T>(op: () => Promise<T>): Promise<T> {
  const run = sandboxQueue.then(op, op);
  sandboxQueue = run.catch(() => {});
  return run;
}

const childrenOfCategory = (n: any): any[] =>
  n?.subcategories || n?.list || n?.children || [];

function findCategoryNode(nodes: any[], id: unknown): any | null {
  for (const n of nodes || []) {
    if (n.id === id) return n;
    const found = findCategoryNode(childrenOfCategory(n), id);
    if (found) return found;
  }
  return null;
}

export function readSandbox(): SandboxRef | null {
  try {
    const raw = localStorage.getItem(SANDBOX_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeSandbox(ref: SandboxRef | null) {
  try {
    if (ref) localStorage.setItem(SANDBOX_KEY, JSON.stringify(ref));
    else localStorage.removeItem(SANDBOX_KEY);
  } catch {
    /* ignore */
  }
}

/** Sélecteur dynamique du bouton Fx de la ligne bac à sable. */
function sandboxFxSelector(): string | null {
  const ref = readSandbox();
  return ref ? `[data-tour-step="cat-fx-${ref.subId}"]` : null;
}

/** Récupère l'arbre de catégories à jour (l'état du store peut être périmé
 *  sur le dashboard). */
async function refreshCategories() {
  try {
    await store.dispatch('app/actionGetListCategories');
  } catch {
    /* ignore */
  }
}

/** Cherche une sous-catégorie bac à sable déjà présente (par nom), dans toutes
 *  les catégories. Renvoie sa réf ou null. */
function findExistingSandbox(): SandboxRef | null {
  const categories = store.state.app?.categories || [];
  const walk = (nodes: any[], parentId: any): SandboxRef | null => {
    for (const n of nodes || []) {
      if (isSandboxName(n.name)) return {subId: n.id, parentId};
      const found = walk(childrenOfCategory(n), n.id);
      if (found) return found;
    }
    return null;
  };
  return walk(categories, null);
}

/** Active le prévisionnel sur le bac à sable (sinon son bouton Fx ne s'affiche
 *  pas : le rendu du Fx exige `forecast_enabled`).
 *  SÛR : on clone l'arbre COURANT COMPLET (qui contient déjà tous les
 *  `forecast_enabled` existants) et on passe SEULEMENT le bac à sable à `true`.
 *  `actionUpdateCategoryForecast` renvoie la liste de toutes les catégories
 *  activées de l'arbre → aucune autre n'est désactivée. On parcourt `.list`
 *  (même clé que `recursiveEnabledCategories` côté store). Si le nœud n'est pas
 *  trouvé, on ne touche à rien. */
async function enableSandboxForecast(subId: string | number) {
  const tree = JSON.parse(JSON.stringify(store.state.app?.categories || []));
  let node: any = null;
  const walk = (nodes: any[]) => {
    for (const n of nodes || []) {
      if (n.id === subId) {
        node = n;
        return;
      }
      walk(n.list || []);
      if (node) return;
    }
  };
  walk(tree);
  if (!node || node.forecast_enabled) return; // introuvable ou déjà activé
  node.forecast_enabled = true;
  await store.dispatch('app/actionUpdateCategoryForecast', tree);
  await refreshCategories();
}

/** Crée (ou réutilise) la sous-catégorie bac à sable. Idempotent.
 *  IMPORTANT : on ne fait jamais confiance à la réf mémorisée en localStorage
 *  (elle peut être périmée : la catégorie a pu être supprimée au cleanup d'un
 *  run précédent). On se fie toujours à l'arbre réel des catégories. */
export function seedSandbox() {
  return enqueueSandboxOp(seedSandboxImpl);
}
async function seedSandboxImpl() {
  // On part de l'arbre à jour, puis on réutilise un bac à sable existant
  // (sinon l'API renvoie « Forbidden to add same category name »).
  await refreshCategories();
  const existing = findExistingSandbox();
  if (existing) {
    writeSandbox(existing);
    await enableSandboxForecast(existing.subId);
    return;
  }
  // Réf périmée éventuelle : on la purge avant de recréer.
  writeSandbox(null);
  const parent = (store.state.app?.categories || [])[0];
  if (!parent) return; // pas d'arborescence → version explicative
  await store.dispatch('app/actionAddCategory', {
    name: SANDBOX_NAME,
    parentId: parent.id,
  });
  await refreshCategories();
  const created = findExistingSandbox();
  if (created) {
    writeSandbox(created);
    // Active le prévisionnel pour que le bouton Fx apparaisse sur sa ligne.
    await enableSandboxForecast(created.subId);
  }
}

function pickTargetCategory(ref: SandboxRef): number | null {
  const cats = store.state.app?.categories || [];
  const parent = findCategoryNode(cats, ref.parentId);
  const sibling = childrenOfCategory(parent).find(
    (c: any) => c.id !== ref.subId && !c.is_alterable && !isSandboxName(c.name),
  );
  if (sibling) return sibling.id;
  const fallback = cats.find(
    (c: any) => !c.is_alterable && !isSandboxName(c.name),
  );
  return fallback ? fallback.id : null;
}

async function clearSandboxFormulas(subId: number) {
  try {
    const formulas = await store.dispatch(
      'app/actionGetCFSList',
      {category_id: subId},
      {root: true},
    );
    for (const formula of formulas || []) {
      await store.dispatch(
        'app/actionDeleteCFS',
        {cfs_id: formula.id},
        {root: true},
      );
    }
  } catch (err) {
    console.warn('[Tour] suppression des formules du bac à sable échouée', err);
  }
}

/** Efface les objectifs manuels saisis sur le bac à sable pendant le tuto
 *  « saisie d'objectifs » (submit(true) / flèche orange). Indispensable AVANT la
 *  suppression : `removeCategory` transfère la donnée du bac à sable vers son
 *  `targetCategoryId` (une vraie catégorie), donc l'objectif fantôme se
 *  retrouverait recopié sur des données réelles et remonterait dans le total du
 *  parent. On vide la ligne d'abord → plus rien à transférer. Best-effort. */
async function clearSandboxAdjustments(subId: number) {
  try {
    await store.dispatch(
      'bank/cleanCategoryAdjustments',
      {category: subId, scenarioId: null, accountIds: null},
      {root: true},
    );
  } catch (err) {
    console.warn('[Tour] effacement objectifs bac à sable échoué', err);
  }
}

/** Supprime TOUS les bacs à sable (par nom), en boucle, pour purger aussi les
 *  orphelins accumulés. Idempotent. */
export function cleanupSandbox() {
  return enqueueSandboxOp(cleanupSandboxImpl);
}
async function cleanupSandboxImpl() {
  await refreshCategories();
  let ref = findExistingSandbox();
  while (ref) {
    // Purge les objectifs manuels AVANT la suppression (sinon ils sont transférés
    // vers la vraie catégorie destination et polluent les données réelles).
    await clearSandboxAdjustments(ref.subId);
    await clearSandboxFormulas(ref.subId);
    const target = pickTargetCategory(ref);
    try {
      await store.dispatch('app/actionRemoveCategory', {
        categoryId: ref.subId,
        parentId: ref.parentId,
        targetCategoryId: target,
      });
    } catch (err) {
      console.warn('[Tour] cleanup bac à sable échoué, réessai au reload', err);
      writeSandbox(ref); // on garde la réf pour réessayer plus tard
      return;
    }
    await refreshCategories();
    ref = findExistingSandbox();
  }
  writeSandbox(null);
  // La catégorie est supprimée en base + le store rafraîchi, mais la grille du
  // dashboard vient d'un autre fetch : on la recharge pour que la ligne
  // « Exemple tutoriel » disparaisse à l'écran tout de suite.
  AppBus.$emit('tour:reload-table');
}

/** Filet de sécurité : à appeler au montage du Dashboard pour purger un résidu
 *  laissé par un rechargement / une sortie brutale pendant le tuto. */
export function cleanupOrphanSandbox() {
  if (window.FygrTour?.isActive?.()) return;
  // On nettoie si une réf est mémorisée OU si un bac à sable orphelin existe.
  if (readSandbox() || findExistingSandbox()) {
    cleanupSandbox();
  }
}

/** Force la vue tableau + déplie le parent du bac à sable (en rafale, car la
 *  bascule de vue peut (re)monter CashTable après le 1er emit). */
function goToTableAndReveal() {
  AppBus.$emit('tour:dashboard-table');
  const parentId = readSandbox()?.parentId;
  let tries = 0;
  const tick = () => {
    AppBus.$emit('tour:expand-category', parentId);
    // Une fois le tableau monté (2e tick) : recharge la donnée pour que le Fx du
    // bac à sable (forecast fraîchement activé) apparaisse.
    if (tries === 1) AppBus.$emit('tour:reload-table');
    if (++tries < 6) setTimeout(tick, 400);
  };
  tick();
}

window.FygrTour.register('forecast-formulas', {
  title: 'Construire votre prévisionnel central',
  page: '/dashboard',
  intro: {
    title: 'Construisons votre prévisionnel central',
    body: 'Posez les bases de votre prévisionnel : vue tableau, création d’une formule, ajustement d’un montant, puis intégration de vos factures.',
    note: 'Vous vous exercez sur une catégorie temporaire, supprimée automatiquement à la fin : vos données réelles ne sont pas modifiées.',
    startLabel: 'Commencer',
    optoutLabel: 'Désactiver les guides automatiques',
    optoutNote:
      'Ils ne s’afficheront plus automatiquement. Vous pourrez les relancer à tout moment depuis « Tutoriels ».',
  },
  onStart: seedSandbox,
  onExit: cleanupSandbox,
  steps: [
    {
      target: '[data-tour-step="dash-view-table"]',
      title: 'Passez en vue tableau',
      body: 'Le prévisionnel se construit dans la vue tableau. Cliquez sur l’icône tableau pour l’afficher.',
      placement: 'bottom',
      // On apprend en faisant : le clic sur le bouton bascule la vue ET fait
      // avancer le tuto.
      clickToAdvance: true,
      // Laisse voir la bascule avant que la bulle ne saute sur le Fx.
      nextDelay: 500,
      onNext: goToTableAndReveal,
    },
    {
      target: sandboxFxSelector,
      title: 'Ouvrez les formules (Fx)',
      body: 'Sur la ligne « Exemple Okimia », cliquez sur le bouton Fx pour gérer les formules de cette sous-catégorie.',
      placement: 'top',
      waitFor: '[data-tour-step="add-formula"]',
      waitTimeout: 6000,
    },
    {
      target: '[data-tour-step="add-formula"]',
      title: 'Ajoutez une formule',
      body: 'Cliquez sur « Ajouter une formule ».',
      // Au-dessus de la cible : on ne masque pas le champ « nom de la formule ».
      placement: 'top',
      waitTimeout: 3000,
      // Avance une fois la pop-up « Ajouter une formule » ouverte.
      waitFor: '[data-tour-step="formula-add-confirm"]',
    },
    {
      // Cible = la carte du popup (et non le wrapper plein écran de BasePopup,
      // sinon la bulle part au bord droit de l'écran). La carte contient le champ
      // « nom » ET le bouton « Ajouter » → tous deux restent interactifs (le
      // bloqueur de clics autorise la cible + ses descendants).
      target: '[data-tour-step="formula-add-popup"] .fy-bp-container',
      // Compteur : de « Ajoutez une formule » jusqu'à « min / max », tout partage
      // le n° 3 (les clics/étapes restent, seul l'affichage est regroupé).
      groupWithPrevious: true,
      title: 'Nommez votre formule',
      body: 'Donnez un nom à votre formule, puis cliquez sur « Ajouter ». Les champs de personnalisation s’afficheront ensuite à droite.',
      // À droite du popup pour ne pas recouvrir le champ de saisie.
      placement: 'right',
      waitTimeout: 6000,
      // Pas de « Suivant » : on avance tout seul quand le panneau Détails (donc
      // la formule créée) apparaît.
      advanceWhen: '[data-tour-step="cfs-valeur"]',
      // On neutralise la croix de fermeture : la cliquer fermerait le popup et
      // casserait le tuto.
      blockWithin: '.fy-bp-close',
    },
    {
      target: '[data-tour-step="cfs-valeur"]',
      groupWithPrevious: true,
      title: 'La valeur (€ ou %)',
      body: 'Saisissez le montant de la formule. Basculez € ↔ % selon que vous voulez un montant fixe ou un pourcentage.',
      placement: 'left',
      clickToAdvance: false,
      waitTimeout: 6000,
    },
    {
      target: '[data-tour-step="cfs-startdate"]',
      groupWithPrevious: true,
      title: 'La date de début',
      body: 'Choisissez la date à partir de laquelle la formule s’applique.',
      placement: 'left',
      clickToAdvance: false,
      // Étape explicative : on surligne sans ouvrir le calendrier (qui
      // recouvrirait la bulle).
      lockTarget: true,
      waitTimeout: 3000,
    },
    {
      target: '[data-tour-step="cfs-recurrence"]',
      groupWithPrevious: true,
      title: 'La récurrence',
      body: 'Définissez à quelle fréquence la formule se répète (mensuelle, trimestrielle…).',
      placement: 'left',
      clickToAdvance: false,
      lockTarget: true,
      waitTimeout: 3000,
    },
    {
      target: '[data-tour-step="cfs-advanced"]',
      groupWithPrevious: true,
      title: 'Les paramètres avancés',
      body: 'Cliquez sur « Paramètres avancés » pour afficher les montants min / max.',
      placement: 'top',
      // Clic utilisateur : déplie la section, puis on attend qu’elle soit visible.
      waitFor: '[data-tour-step="cfs-minmax"]',
      waitTimeout: 3000,
    },
    {
      target: '[data-tour-step="cfs-minmax"]',
      groupWithPrevious: true,
      title: 'Montant min / max',
      body: 'Bornez la prévision : un montant minimum et/ou maximum à ne pas dépasser.',
      placement: 'top',
      clickToAdvance: false,
      waitTimeout: 3000,
    },
    {
      target: '[data-tour-step="formula-actions"]',
      title: 'Modifier, désactiver, supprimer',
      body: 'Une fois la formule créée, vous aurez la possibilité par la suite de la modifier, la désactiver ou même la supprimer.',
      placement: 'top',
      clickToAdvance: false,
      lockTarget: true,
      waitTimeout: 3000,
    },
    {
      target: '[data-tour-step="add-formula"]',
      title: 'Des formules superposables',
      body: 'À noter que les formules sont superposables : il vous est ainsi possible de créer plusieurs formules de prévisionnel pour une seule sous-catégorie.',
      placement: 'top',
      clickToAdvance: false,
      lockTarget: true,
      waitTimeout: 3000,
    },
  ],
  celebration: {
    title: 'Votre prévisionnel prend forme',
    body: 'La catégorie d’exemple va être supprimée. Pour aller plus loin :',
    actionPanel: {
      title: 'Pour aller plus loin',
      body: 'Continuez à fiabiliser votre prévisionnel central.',
      actions: [
        {
          label: 'Ajuster un montant à la main',
          body: 'Modifiez directement une cellule du tableau.',
          icon: 'edit',
          // On ne le repropose pas si l'utilisateur en vient déjà (état back).
          hideIf: () => isTourCompleted('forecast-objectives'),
          onSelect: () => {
            // Ferme la popup FORMULES puis enchaîne sur le tuto saisie d'objectifs
            // (qui a besoin de la vue tableau dégagée). Délai = laisser la popup
            // se fermer et le tuto courant sortir proprement.
            AppBus.$emit('tour:close-formula');
            setTimeout(() => window.FygrTour.start('forecast-objectives'), 500);
          },
        },
        {
          label: 'Intégrer vos factures',
          body: 'Projetez vos factures clients et fournisseurs.',
          icon: 'receipt_long',
          // Enchaîne sur le tuto factures (échéances), lancé via ?tour= au
          // montage de la page /invoices.
          href: '/invoices?tour=invoices-overview&from=recap',
        },
      ],
    },
  },
});

export {};
